"use client";

import { c as _c } from "react/compiler-runtime";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, CarouselDots } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { GeoLocation } from "@/types/location";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import matchbusIcon from "@/assets/images/matchbus_icon.jpg";
import Image from "next/image";
import { useTranslation } from "@/app/i18n/client";
import { createCompanyURI } from "@/components/Util/AdvertiserHelpers";
import { sendGTMEvent } from "@/components/Tracking/TagManagerWrapper";
import { AnalyticsActions, AnalyticsCategories, AnalyticsEventType, AnalyticsLabels } from "@/constants/analytics";
import Autoplay from "embla-carousel-autoplay";
import { api } from "@/utils/trpc/react";
const carouselOpts = {
  align: "start",
  loop: true
} as const;
export function AdvertiserCarousel(t0) {
  const $ = _c(15);
  const {
    startLocation,
    endLocation
  } = t0;
  const carouselRef = useRef(null);
  const [isAutoplayActive, setIsAutoplayActive] = useState(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (carouselRef.current) {
        const mouseEnterCallback = () => setIsAutoplayActive(false);
        const mouseLeaveCallback = () => setIsAutoplayActive(true);
        const controller = new AbortController();
        carouselRef.current.addEventListener("mouseenter", mouseEnterCallback, {
          signal: controller.signal
        });
        carouselRef.current.addEventListener("mouseleave", mouseLeaveCallback, {
          signal: controller.signal
        });
        return () => {
          controller.abort();
        };
      }
    };
    t2 = [carouselRef];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== isAutoplayActive) {
    t3 = Autoplay({
      delay: 5000,
      active: isAutoplayActive
    });
    $[2] = isAutoplayActive;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = [t3];
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CarouselPrevious className="hidden lg:block" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== endLocation || $[8] !== startLocation) {
    t6 = <CarouselContent><CarouselElements startLocation={startLocation} endLocation={endLocation} /></CarouselContent>;
    $[7] = endLocation;
    $[8] = startLocation;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CarouselNext className="hidden lg:block" />;
    t8 = <CarouselDots className="block scale-75 lg:hidden" />;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t4 || $[13] !== t6) {
    t9 = <Carousel opts={carouselOpts} ref={carouselRef} plugins={t4} className="mx-auto mb-8 w-full">{t5}{t6}{t7}{t8}</Carousel>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
type CarouselElementViewProps = {
  title: string;
  description: string;
  tag?: string;
  onClick?: () => void;
  link?: ReturnType<typeof createCompanyURI>;
  imageClassName?: string;
  className?: string;
  imageSrc: string;
  imageAlt: string;
};
export function CarouselElementView(t0) {
  const $ = _c(24);
  const {
    title,
    description,
    tag,
    onClick,
    link,
    className,
    imageClassName,
    imageSrc,
    imageAlt
  } = t0;
  const t1 = link ?? "#";
  let t2;
  if ($[0] !== imageClassName) {
    t2 = cn("aspect-advertiserCardImg h-auto w-full bg-slate-100 object-fill object-top", imageClassName);
    $[0] = imageClassName;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== imageAlt || $[3] !== imageSrc || $[4] !== t2) {
    t3 = <Image src={imageSrc} width={350} height={263} loading="lazy" alt={imageAlt} className={t2} />;
    $[2] = imageAlt;
    $[3] = imageSrc;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== tag) {
    t4 = tag && <div className="absolute bottom-2 right-4 p-2 text-sm font-bold text-action">{tag}</div>;
    $[6] = tag;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== title) {
    t5 = <h3 className="mt-0 text-start text-lg font-bold">{title}</h3>;
    $[8] = title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== description) {
    t6 = <div className="text-normal leading-snug text-secondary">{description}</div>;
    $[10] = description;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="h-full bg-babyBlue p-4 pb-8 text-start">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== onClick || $[16] !== t1 || $[17] !== t3 || $[18] !== t4 || $[19] !== t7) {
    t8 = <Link href={t1} onClick={onClick} className="relative flex aspect-advertiserCardSm h-full w-full flex-col justify-between gap-0 overflow-hidden rounded-2xl text-secondary transition-transform delay-0 duration-300 hover:scale-95 sm:aspect-advertiserCardXl xxl:aspect-advertiserCardXl">{t3}{t4}{t7}</Link>;
    $[15] = onClick;
    $[16] = t1;
    $[17] = t3;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== className || $[22] !== t8) {
    t9 = <CarouselItem className={className}>{t8}</CarouselItem>;
    $[21] = className;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
function CarouselElements(t0) {
  const $ = _c(17);
  const {
    startLocation,
    endLocation
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["common", "dataerrors"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t,
    i18n
  } = useTranslation(t1);
  let t2;
  if ($[1] !== endLocation || $[2] !== startLocation) {
    t2 = {
      startLocation,
      endLocation
    };
    $[1] = endLocation;
    $[2] = startLocation;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [advertisers, t3] = api.advertiserlocation.GetStartpageAdvertisers.useSuspenseQuery(t2);
  const {
    isError
  } = t3;
  if (isError) {
    const t4 = t("dataerrors:ERRORS.CAROUSEL.FETCH_ERROR.TITLE");
    const t5 = t("dataerrors:ERRORS.CAROUSEL.FETCH_ERROR.DESCRIPTION");
    let t6;
    if ($[4] !== t4 || $[5] !== t5) {
      t6 = <CarouselElementView title={t4} description={t5} imageSrc={matchbusIcon.src} imageAlt="error" className="basis-full xs:basis-1/2 xl:basis-1/3 xxl:basis-1/4" />;
      $[4] = t4;
      $[5] = t5;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    return t6;
  }
  if (!advertisers || advertisers.length === 0) {
    const t4 = t("dataerrors:ERRORS.CAROUSEL.NO_DATA.TITLE");
    const t5 = t("dataerrors:ERRORS.CAROUSEL.NO_DATA.DESCRIPTION");
    let t6;
    if ($[7] !== t4 || $[8] !== t5) {
      t6 = <CarouselElementView title={t4} description={t5} imageSrc={matchbusIcon.src} imageAlt="nodata" className="basis-full xs:basis-1/2 xl:basis-1/3 xxl:basis-1/4" />;
      $[7] = t4;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  const sendClickAnalytics = _temp;
  let t4;
  if ($[10] !== advertisers || $[11] !== i18n || $[12] !== t) {
    let t5;
    if ($[14] !== i18n || $[15] !== t) {
      t5 = advertiser => {
        const company = advertiser.company;
        const linkDst = createCompanyURI(company, i18n.language);
        return <CarouselElementView key={"advertiser-item-" + advertiser.company.id} link={linkDst} onClick={sendClickAnalytics} imageSrc={company.startpageInfo.teaserPicture} title={company.startpageInfo.title} description={company.startpageInfo.description} imageAlt={company.publicData.companyName} tag={t("HOME.STARTPAGEADVERTISERS.MORE") + ": " + company.publicData.companyName} className="basis-full xs:basis-1/2 xl:basis-1/3 xxl:basis-1/4" />;
      };
      $[14] = i18n;
      $[15] = t;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    t4 = advertisers.map(t5);
    $[10] = advertisers;
    $[11] = i18n;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function _temp() {
  sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.advertiserCarousel, AnalyticsLabels.user, AnalyticsActions.adClick);
}